'use client';

import { useAuthUser } from '@/hooks/useAuthUser';
import { useEffect, useState } from 'react';
import { type EventHandler, useJoyride } from 'react-joyride';
import type { Step } from 'react-joyride';
import { TourTooltip } from './TourTooltip';
import { getTourSeen, setTourSeen } from './tourStorage';

interface JoyrideTourProps {
  /** Unique tour name — also used as the localStorage "seen" key. */
  name: string;
  /** Steps to run. */
  steps: Step[];
  /** Whether the tour should auto-launch (default true). */
  auto?: boolean;
}

/**
 * Self-contained, reusable tour element. Auto-launches on a page exactly when
 * that page's tour has not been seen yet in this browser — tracked per tour in
 * localStorage under `tour:seen:<name>`, and only for logged-in users (the
 * single auth gate shared by every tour lives here). No session or viewport
 * throttling: an unseen tour fires on every visit until the user finishes or
 * skips it.
 */
export function JoyrideTour({ name, steps, auto = true }: JoyrideTourProps) {
  // Start false on every render (server and first client paint) so the
  // server-rendered HTML and the client's pre-effect render always agree —
  // `getTourSeen` can only be answered on the client (it reads
  // localStorage), so deciding it during the initial render would make the
  // server guess "not seen" and the client sometimes correct that to
  // "seen", a mismatch. Flip it after mount instead, once we can actually
  // check.
  const [run, setRun] = useState(false);

  const { isAuthenticated } = useAuthUser();

  // Authoritative in BOTH directions, and `name` is a real dependency: the
  // kancelaria tour swaps its name between `kancelaria-spisy` and
  // `-klienti` on a client-side tab switch without remounting. Only setting
  // `true` here would leave a dismissed tour running when the user switches
  // to a tab whose tour was already seen.
  useEffect(() => {
    const eligible = auto && isAuthenticated && !getTourSeen(name);
    setRun(eligible);
  }, [name, auto, isAuthenticated]);

  const handleEvent: EventHandler = (data) => {
    if (data.status === 'finished' || data.status === 'skipped') {
      setTourSeen(name);
      setRun(false);
    }
  };

  const { Tour } = useJoyride({
    steps,
    run,
    continuous: true,
    scrollToFirstStep: true,
    tooltipComponent: TourTooltip,
    onEvent: handleEvent,
    options: {
      showProgress: true,
      buttons: ['back', 'skip', 'primary'],
      spotlightPadding: 6,
      spotlightRadius: 14,
      zIndex: 10_000,
    },
  });

  return <>{run ? Tour : null}</>;
}
